import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Plus } from 'lucide-react';
import { useLocalStorage } from 'usehooks-ts';
import { TenantType } from '@bliks-io/contracts';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import { Button, Label, Page, PageContent, Switch } from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { CreateTenantDialog } from './CreateTenantDialog';
import { CloneTenantDialog } from './CloneTenantDialog';
import { TenantsTable } from './TenantsTable';
import { TENANT_TYPE_LABEL_KEYS } from './components/TenantTypeBadge';
import { isTenantExpired } from './components/TenantExpiryBadge';

const TYPE_FILTER_OPTIONS: Array<{ value: TenantType | 'all'; labelKey: string }> = [
  { value: 'all', labelKey: 'tenantAdmin.filterAll' },
  { value: 'demo', labelKey: TENANT_TYPE_LABEL_KEYS.demo },
  { value: 'partner', labelKey: TENANT_TYPE_LABEL_KEYS.partner },
  { value: 'customer', labelKey: TENANT_TYPE_LABEL_KEYS.customer }
];

/**
 * Alle Arbeitsbereiche.
 *
 * Die Tabelle selbst steht daneben ({@link TenantsTable}) und wird auch auf
 * der Kontoseite benutzt, dort auf ein Konto gefiltert. Hier bleibt, was zu
 * keiner Zeile gehoert: die Einstiege zum Anlegen, der Typfilter und der
 * Schalter, der Abgelaufene ausblendet.
 */
export function TenantListPage() {
  useDocumentTitle('pageTitles.tenants');
  const { t } = useTranslation();
  const { data } = useListTenants();

  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [createDemoOpen, setCreateDemoOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TenantType | 'all'>('all');
  const [hideExpired, setHideExpired] = useLocalStorage(
    'bliks:datatable:adminTenants:hideExpired',
    false
  );

  const filteredTenants = useMemo(
    () =>
      (data?.tenants ?? []).filter(
        (tenant) =>
          (typeFilter === 'all' || tenant.tenantType === typeFilter) &&
          (!hideExpired || !isTenantExpired(tenant.expiresAt))
      ),
    [data?.tenants, typeFilter, hideExpired]
  );

  return (
    <Page>
      <PageContent>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('tenantAdmin.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('tenantAdmin.description')}</p>
          </div>
          <div className="flex gap-2">
            {/* Eigener Einstieg, weil eine Demo eine eigene Absicht ist: sie
                entsteht aus einer Vorlage, nicht aus dem Nichts. Ohne ihn
                musste man wissen, dass sie hinter „Duplizieren" in der Zeile
                eines fremden Arbeitsbereichs steckt. */}
            <Button variant="outline" onClick={() => setCreateDemoOpen(true)}>
              <Copy className="w-4 h-4 mr-2" />
              {t('tenantAdmin.createDemo')}
            </Button>
            <Button onClick={() => setCreateTenantOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('tenantAdmin.createTenant')}
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {TYPE_FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="small"
              variant={typeFilter === option.value ? 'primary' : 'outline'}
              onClick={() => setTypeFilter(option.value)}
            >
              {t(option.labelKey)}
            </Button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Label htmlFor="hide-expired-tenants">{t('tenantAdmin.hideExpired')}</Label>
            <Switch
              id="hide-expired-tenants"
              checked={hideExpired}
              onCheckedChange={setHideExpired}
            />
          </div>
        </div>

        <TenantsTable
          tenants={filteredTenants}
          tableId="adminTenants"
          initialColumnVisibility={{ slug: false, members: false, processes: false }}
        />

        <CreateTenantDialog open={createTenantOpen} onOpenChange={setCreateTenantOpen} />

        {/* Ohne Vorlage: den Arbeitsbereich, von dem kopiert wird, waehlt der
            Dialog. Bedingt gehaengt, damit jedes Oeffnen frisch beginnt. */}
        {createDemoOpen && (
          <CloneTenantDialog
            open
            onOpenChange={setCreateDemoOpen}
            candidates={data?.tenants ?? []}
          />
        )}
      </PageContent>
    </Page>
  );
}
